import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Observable } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../../shared/toastr.service";
import {
  GET_LINKS,
  GetLinksSuccessAction,
  GetLinksFailureAction,
  CHANGE_LINK,
  ChangeLinkAction,
  ChangeLinkSuccessAction,
  ChangeLinkFailureAction,
  REMOVE_LINK,
  RemoveLinkAction,
  RemoveLinkSuccessAction,
  RemoveLinkFailureAction,
  ADD_LINK,
  ADD_LINK_FAILURE,
  AddLinkAction,
  AddLinkSuccessAction,
  AddLinkFailureAction,
} from "./links.actions";
import { LinksService } from "./links.service";
import { AddLinkObject, Links, ChangeLinkObject } from "./links.interface";
import { MatDialog } from "@angular/material/dialog";
import { LimitPlanDialogComponent } from "../../shared/limit-plan-dialog/limit-plan-dialog.component";

@Injectable()
export class LinksEffects {
  getLinks$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(GET_LINKS),
      switchMap(() => {
        return this.linksService.getLinks().pipe(
          switchMap((response: Links) => {
            return [new GetLinksSuccessAction(response)];
          }),
          catchError((error) => of(new GetLinksFailureAction(error))),
        );
      }),
    );
  });

  changeLink$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_LINK),
      map((action: ChangeLinkAction) => action.payload),
      switchMap((payload: ChangeLinkObject) => {
        return this.linksService.changeLink(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant("addons.links.editSuccess") as string,
            );
            return [new ChangeLinkSuccessAction(payload)];
          }),
          catchError((error) => of(new ChangeLinkFailureAction(error))),
        );
      }),
    ),
  );

  removeLink$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(REMOVE_LINK),
      map((action: RemoveLinkAction) => action.payload),
      switchMap((payload: string) => {
        return this.linksService.removeLink(payload).pipe(
          switchMap(() => {
            this.toastr.success(
              this.translate.instant("addons.links.deleteSuccess") as string,
            );
            return [new RemoveLinkSuccessAction(payload)];
          }),
          catchError((error) => of(new RemoveLinkFailureAction(error))),
        );
      }),
    ),
  );

  addLink$: Observable<unknown> = createEffect(() =>
    this.actions$.pipe(
      ofType(ADD_LINK),
      map((action: AddLinkAction) => action.payload),
      switchMap((payload: AddLinkObject) => {
        return this.linksService.addLink(payload).pipe(
          switchMap((response) => {
            this.toastr.success(
              this.translate.instant("addons.links.addSuccess") as string,
            );
            return [new AddLinkSuccessAction(response)];
          }),
          catchError((error) => of(new AddLinkFailureAction(error))),
        );
      }),
    ),
  );

  addLinkFailure$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ADD_LINK_FAILURE),
        tap((action: AddLinkFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            errorMessage.includes("LINK_LIMIT_REACHED")
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "link" },
            });
          } else {
            this.toastr.error(
              this.translate.instant("addons.links.addError") as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private linksService: LinksService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {}
}
